import { LOCALE_FR, LOCALE_NL, type Locale, SUPPORTED_LOCALES } from '@/i18n/main'
import type { ContractRequest } from '@/types/contractRequests'
import { ArchivedContractStatuses } from '@/types/contractStatuses'
import { type Contract, PaidUpStoppingReason } from '@/types/contracts'
import { ProductType } from '@/types/productTypes'
import { formatAddress } from '@/utils/formatters'
import { useI18n } from 'vue-i18n'

export const concernsDetails = (contract: Contract | ContractRequest) => {
  if (contract.contractType === 'contract') {
    return { primary: groupAliases(contract.aliases) }
  }
  switch (contract.productType) {
    case ProductType.Car:
      return {
        primary: `${contract.brand} ${contract.model}`,
        secondary: contract.licensePlate ? contract.licensePlate : ''
      }
    case ProductType.Motorbike:
      return {
        primary: `${contract.brand} ${contract.model}`,
        secondary: contract.licensePlate ? contract.licensePlate : ''
      }
    case ProductType.Bicycle:
      return {
        primary: contract.brand
      }
    case ProductType.House:
      return {
        secondary:
          contract.address && contract.address.street && contract.address.postalCode
            ? formatAddress(contract.address)
            : '-'
      }
    // case 'hospitalisation':
    // case 'life':
    // case 'family':
    // case 'travel':
    // case 'civil_liability':
    // case 'legal_expenses':
    //   return {
    //     primary: `${contract.policy_owner.first_name} ${contract.policy_owner.last_name}`
    //   }
    // case 'digital_object':
    //   return {
    //     primary: contract.object
    //   }
    default:
      return {
        primary: ''
      }
  }
}

export const contractLabel = (contract: Contract | ContractRequest) => {
  const { locale, t } = useI18n()
  if (contract.productShortLabel && SUPPORTED_LOCALES.includes(locale.value as Locale)) {
    if (locale.value === LOCALE_FR) return contract.productShortLabel.fr
    else if (locale.value === LOCALE_NL) return contract.productShortLabel.nl
    else return contract.productShortLabel.en
  } else {
    return t(`terms.contract.product_types.${contract.productType}`)
  }
}

export const claimLabel = (contract: Contract) => {
  const { locale, t } = useI18n()
  if (contract.productShortLabel && SUPPORTED_LOCALES.includes(locale.value as Locale)) {
    let productShortLabel = ''

    if (locale.value === LOCALE_FR) productShortLabel = contract.productShortLabel.fr
    else if (locale.value === LOCALE_NL) productShortLabel = contract.productShortLabel.nl
    else productShortLabel = contract.productShortLabel.en

    return t(`terms.claim.type`, { productLine: productShortLabel })
  } else {
    return t(`terms.claim.types.${contract.productType}`)
  }
}

export const contractPremium = (contract: Contract | ContractRequest) => {
  return contract.fullPackageOfferPrice || contract.payment.premium
}

export const groupAliases = (aliases: string[]) => {
  return aliases.filter((e) => e).join(', ')
}

interface Label {
  fr: string
  nl: string
  en: string
}

export const labelTranslation = (label: Label, locale: string) => {
  if (locale === 'fr') {
    return label.fr
  } else if (locale === 'nl') {
    return label.nl
  } else {
    return label.en
  }
}

export const isLifeContractTerminatedWithPaidUpReason = (contract: Contract): boolean => {
  const isTerminated = ArchivedContractStatuses.includes(contract.status)
  const hasStoppingReason = Boolean(contract.stoppingReason)
  const isPaidUpReason = hasStoppingReason && PaidUpStoppingReason.includes(contract.stoppingReason!)

  return isTerminated && isPaidUpReason
}

export const lifeContractsTerminatedWithPaidUpReason = (contracts: Contract[]) => {
  return contracts.filter((contract) => isLifeContractTerminatedWithPaidUpReason(contract))
}

export const filterContractsWithoutLifePaidUpStopped = (contracts: Contract[]) => {
  return contracts.filter((contract) => !isLifeContractTerminatedWithPaidUpReason(contract))
}
